import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { initialAnswers, initialCompletedSteps, initialResumeStep } from '../data/initialAnswers';
import { scenarioOverrides } from '../data/scenarios';
import { initialDocuments } from '../data/documents';
import { initialMessages, initialTasks } from '../data/loanStatus';
import type { Answers, BorrowerScenario, BorrowerTask, DocumentItem, Message, PricingStatus } from '../types/application';
interface PortalState {
  answers: Answers;
  update: (patch: Partial<Answers>) => void;
  completed: string[];
  markComplete: (stepId: string) => void;
  lastStep: string;
  setLastStep: (id: string) => void;
  saving: boolean;
  savedAt: number;
  documents: DocumentItem[];
  uploadDocument: (id: string, files: string[]) => void;
  tasks: BorrowerTask[];
  completeTask: (id: string) => void;
  messages: Message[];
  sendMessage: (body: string) => void;
  selectedOption: string;
  selectOption: (id: string) => void;
  pricingStatus: PricingStatus;
  creditAuthorized: boolean;
  authorizeCredit: () => void;
  submitted: boolean;
  submitApplication: () => void;
}
const PortalContext = createContext<PortalState | null>(null);
interface PortalProviderProps {
  children: React.ReactNode;
  scenario?: BorrowerScenario;
  initialPricingStatus?: PricingStatus;
}
export function PortalProvider({
  children,
  scenario = 'underContract',
  initialPricingStatus = 'selected'
}: PortalProviderProps) {
  const [answers, setAnswers] = useState<Answers>(() => ({
    ...initialAnswers,
    ...scenarioOverrides[scenario]
  }));
  const [completed, setCompleted] = useState<string[]>(initialCompletedSteps);
  const [lastStep, setLastStep] = useState(initialResumeStep);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(Date.now() - 1000 * 60 * 60 * 26);
  const [documents, setDocuments] = useState<DocumentItem[]>(initialDocuments);
  const [tasks, setTasks] = useState<BorrowerTask[]>(initialTasks);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [selectedOption, setSelectedOption] = useState('');
  const [pricingStatus, setPricingStatus] = useState<PricingStatus>(initialPricingStatus);
  const [creditAuthorized, setCreditAuthorized] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const saveTimer = useRef<number>();
  const autosave = useCallback(() => {
    setSaving(true);
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      setSaving(false);
      setSavedAt(Date.now());
    }, 700);
  }, []);
  const update = useCallback((patch: Partial<Answers>) => {
    setAnswers(prev => ({
      ...prev,
      ...patch
    }));
    autosave();
  }, [autosave]);
  const markComplete = useCallback((id: string) => {
    setCompleted(prev => prev.includes(id) ? prev : [...prev, id]);
  }, []);
  const uploadDocument = useCallback((id: string, files: string[]) => {
    setDocuments(prev => prev.map(d => d.id === id ? {
      ...d,
      status: 'uploaded',
      files,
      note: undefined
    } : d));
    window.setTimeout(() => {
      setDocuments(prev => prev.map(d => d.id === id && d.status === 'uploaded' ? {
        ...d,
        status: 'reviewing'
      } : d));
    }, 4000);
  }, []);
  const completeTask = useCallback((id: string) => {
    setTasks(prev => prev.map(t => t.id === id ? {
      ...t,
      done: true
    } : t));
  }, []);
  const sendMessage = useCallback((body: string) => {
    const now = new Date();
    setMessages(prev => [...prev, {
      id: `m-${now.getTime()}`,
      author: 'borrower',
      body,
      time: `Today · ${now.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })}`
    }]);
  }, []);

  // Borrower selection only records intent. It never floats or locks a rate.
  const selectOption = useCallback((id: string) => {
    setSelectedOption(id);
    setPricingStatus('selected');
  }, []);
  const authorizeCredit = useCallback(() => setCreditAuthorized(true), []);

  // The single "Application Complete" event. Lead → Opportunity conversion hangs off this.
  const submitApplication = useCallback(() => setSubmitted(true), []);
  const value = useMemo<PortalState>(() => ({
    answers,
    update,
    completed,
    markComplete,
    lastStep,
    setLastStep,
    saving,
    savedAt,
    documents,
    uploadDocument,
    tasks,
    completeTask,
    messages,
    sendMessage,
    selectedOption,
    selectOption,
    pricingStatus,
    creditAuthorized,
    authorizeCredit,
    submitted,
    submitApplication
  }), [answers, update, completed, markComplete, lastStep, saving, savedAt, documents, uploadDocument, tasks, completeTask, messages, sendMessage, selectedOption, selectOption, pricingStatus, creditAuthorized, authorizeCredit, submitted, submitApplication]);
  return <PortalContext.Provider value={value} data-id="element-725" data-mp="17wurd33pdnbz">{children}</PortalContext.Provider>;
}
export function usePortal(): PortalState {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal must be used inside PortalProvider');
  return ctx;
}